// Funciones para manejar Promos (notas de categoría "Promos")
import { supabase } from './client';

/**
 * Crear una orden desde un promo
 */
export const createOrderFromPromo = async (
  noteId: string,
  buyerUserId: string,
  transportationType: string,
  itemsJson: string,
  deliveryFee: number,
  deliveryDistance: number,
  totalAmount: number,
  locationData: {
    pickup_latitude: number;
    pickup_longitude: number;
    pickup_address_name: string;
    delivery_latitude: number;
    delivery_longitude: number;
    delivery_address_name: string;
  },
  subtotal?: number,
  systemFee?: number,
  hunterFee?: number
): Promise<any> => {

  // Intento primario: llamar RPC con parámetros extendidos (si la DB tiene la función actualizada)
  try {
    const { data, error, status, statusText } = await supabase.rpc('create_order_from_promo', {
      p_note_id: noteId,
      p_buyer_user_id: buyerUserId,
      p_transportation_type: transportationType,
      p_items_json: itemsJson,
      p_delivery_fee: deliveryFee,
      p_delivery_distance: deliveryDistance,
      p_total_amount: totalAmount,
      p_pickup_latitude: locationData.pickup_latitude,
      p_pickup_longitude: locationData.pickup_longitude,
      p_pickup_address_name: locationData.pickup_address_name,
      p_delivery_latitude: locationData.delivery_latitude,
      p_delivery_longitude: locationData.delivery_longitude,
      p_delivery_address_name: locationData.delivery_address_name,
      p_subtotal: subtotal,
      p_system_fee: systemFee,
      p_hunter_fee: hunterFee,
    });

    if (error) {
      // Si la función no existe con estos parámetros, tirará error. Dejamos que el catch lo maneje abajo.
      const errMsg = (error.message || '').toString();
      // Si el error sugiere que la función no fue encontrada, lanzar para entrar al fallback.
      if (errMsg.includes('Could not find function') || errMsg.includes('function public.create_order_from_promo') ) {
        throw error;
      }

      console.error('DEBUG [createOrderFromPromo] RPC Error DETAILED (extended):', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        status,
        statusText,
      });
      return { success: false, error: error.message, details: error.details, code: error.code };
    }

    return data;
  } catch (err: any) {
    // Fallback: llamar la versión antigua de la función sin los parámetros nuevos
    console.warn('[createOrderFromPromo] Extended RPC failed, attempting legacy RPC call:', err?.message || err);
    try {
      // Legacy / alternative signature: some DBs have a different param ordering/names.
      // Map parameters using the names found in the schema-cache error so the RPC matches.
      const { data, error } = await supabase.rpc('create_order_from_promo', {
        // many DBs expect buyer first (p_buyer_user_id) instead of note first
        p_buyer_user_id: buyerUserId,
        p_delivery_address_name: locationData.delivery_address_name,
        p_delivery_distance: deliveryDistance,
        p_delivery_fee: deliveryFee,
        p_delivery_latitude: locationData.delivery_latitude,
        p_delivery_longitude: locationData.delivery_longitude,
        p_hunter_fee: hunterFee,
        p_items_json: itemsJson,
        p_note_id: noteId,
        p_pickup_address_name: locationData.pickup_address_name,
        p_pickup_latitude: locationData.pickup_latitude,
        p_pickup_longitude: locationData.pickup_longitude,
        p_subtotal: subtotal,
        p_system_fee: systemFee,
        p_total_amount: totalAmount,
        p_transportation_type: transportationType,
      });

      if (error) {
        console.error('DEBUG [createOrderFromPromo] Legacy RPC Error:', error);
        return { success: false, error: error.message, details: error.details, code: error.code };
      }

      return data;
    } catch (finalErr) {
      console.error('[createOrderFromPromo] Both extended and legacy RPC calls failed:', finalErr);
      return { success: false, error: finalErr?.message || String(finalErr) };
    }
  }
};

/**
 * Votar en un promo (upvote/downvote)
 */
export const voteOnPromoNote = async (
  noteId: string,
  userId: string,
  voteType: 'upvote' | 'downvote'
): Promise<any> => {

  try {
    // Obtener el voto existente
    const { data: existingVote, error: selectError } = await supabase
      .from('note_votes')
      .select('vote_type')
      .eq('note_id', noteId)
      .eq('user_id', userId)
      .maybeSingle();


    // Si existe voto anterior, eliminarlo
    if (existingVote) {
      const { error: deleteError } = await supabase
        .from('note_votes')
        .delete()
        .eq('note_id', noteId)
        .eq('user_id', userId);

      if (deleteError) {
        console.error('[voteOnPromoNote] DELETE ERROR:', deleteError);
        throw deleteError;
      }
    }

    // Insertar nuevo voto
    const { error: insertError } = await supabase
      .from('note_votes')
      .insert({
        note_id: noteId,
        user_id: userId,
        vote_type: voteType,
      });

    if (insertError) {
      console.error('[voteOnPromoNote] INSERT ERROR:', insertError);
      throw insertError;
    }

    
    // El trigger se encarga de actualizar automáticamente:
    // - upvotes/downvotes (COUNT de votos en note_votes)
    // - hotness_score (para notas de categoría Promos)

    return { success: true };
  } catch (error) {
    console.error('[voteOnPromoNote] Error:', error);
    throw error;
  }
};

/**
 * Completar entrega de promo y registrar comisión
 */
export const completePromoDelivery = async (
  orderId: string,
  driverId: string,
  receiptPhotoUrl: string
): Promise<any> => {

  const { data, error } = await supabase.rpc('complete_promo_delivery', {
    p_order_id: orderId,
    p_driver_id: driverId,
    p_receipt_photo_url: receiptPhotoUrl,
  });

  if (error) {
    console.error('[completePromoDelivery] Error:', error);
    throw error;
  }

  return data;
};

/**
 * Obtener settlement ledger de un driver
 */
export const getDriverSettlementLedger = async (driverId: string): Promise<any> => {

  const { data, error } = await supabase.rpc('get_driver_settlement_ledger', {
    p_driver_id: driverId,
  });

  if (error) {
    console.error('[getDriverSettlementLedger] Error:', error);
    throw error;
  }

  return data;
};

/**
 * Obtener categoría Promos
 */
export const getPromosCategory = async () => {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name')
    .eq('name', 'Promos')
    .single();

  if (error) {
    console.error('[getPromosCategory] Error:', error);
    throw error;
  }

  return data;
};
/**
 * Remover voto de un promo
 */
export const removeVoteFromPromo = async (
  noteId: string,
  userId: string
): Promise<any> => {

  const { data, error } = await supabase
    .from('note_votes')
    .delete()
    .eq('note_id', noteId)
    .eq('user_id', userId);

  if (error) {
    console.error('[removeVoteFromPromo] Error:', error);
    throw error;
  }

  return data;
};

/**
 * Obtener voto actual del usuario en un promo
 */
export const getUserVoteOnPromo = async (
  noteId: string,
  userId: string
): Promise<'upvote' | 'downvote' | null> => {

  const { data, error } = await supabase
    .from('note_votes')
    .select('vote_type')
    .eq('note_id', noteId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('[getUserVoteOnPromo] Error:', error);
    throw error;
  }

  return data?.vote_type as 'upvote' | 'downvote' || null;
};